import { useMutation, useQueryClient } from '@tanstack/react-query';

import { secretService } from '@/services/secret-service';
import { sensitiveContentService } from '@/services/sensitive-content-service';
import { pollForResource, pollForResourceGone } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { secretQueryKeys } from '@/hooks/scoped/secrets/query-keys';

import {
  sensitiveContentMutationKeys,
  sensitiveContentQueryKeys,
} from './query-keys';

type PromoteVariables = { connectorId: UUID; id: string; name: string };

/**
 * Promote a row into a secret. Resolves the new secret's ID once the read
 * models show the row gone and the secret present, or null when the row was
 * already gone. Sends only a name and returns only an ID, so a plain mutation.
 */
export function usePromoteSensitiveContent(tenantId: UUID) {
  const qc = useQueryClient();

  return useMutation<UUID | null, Error, PromoteVariables>({
    mutationKey: sensitiveContentMutationKeys.promote(tenantId),
    mutationFn: async ({ connectorId, id, name }) => {
      const secretId = await sensitiveContentService.promote(
        tenantId,
        connectorId,
        id,
        name
      );
      if (secretId === null) return null;
      await pollForResourceGone(() =>
        sensitiveContentService.get(tenantId, connectorId, id)
      );
      await pollForResource(() => secretService.findSecret(tenantId, secretId));
      return secretId;
    },
    onSuccess: (secretId) => {
      qc.invalidateQueries({
        queryKey: sensitiveContentQueryKeys.list(tenantId),
      });
      if (secretId !== null) {
        qc.invalidateQueries({ queryKey: secretQueryKeys.list(tenantId) });
      }
    },
  });
}
